import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  Stack,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "react-qr-code";
import CurrentUserContext from "../../../contexts/CurrentUserContext";
import {
  PreviewType,
  ReducerAction,
  useMobilePreviewDispatch,
} from "../../../contexts/MobilePreviewContext";
import {
  BUILD_NAVIGATION,
  BUILD_THEME,
  ENGAGE_FEED_CREATE,
  ENGAGE_NOTIFICATIONS,
  SETTINGS_INTEGRATIONS,
} from "../../../utils/routes";
import GetStartedTab2BottomSheet from "./GetStartedTab2BottomSheet";

const steps = [
  {
    number: 1,
    title: "Set Up",
    subtitle: "Customize your app's appearance and settings",
    messageRange: ["1", "8"], // Messages 1-8 are setup related
  },
  {
    number: 2,
    title: "App Store Setup",
    subtitle: "Setup Apple and Google accounts",
    messageRange: ["9", "11"], // Messages 9-11 are app store setup
  },
  {
    number: 3,
    title: "Submit",
    subtitle: "Review and publish your app",
    messageRange: ["12", "15"], // Messages 12-15 are submission related
  },
  {
    number: 4,
    title: "Launch",
    subtitle: "Release and market your app",
    messageRange: ["16", "19"], // Messages 16-19 are launch related
  },
];

interface Message {
  id: string;
  text: string | React.ReactNode;
  sender: "bot" | "user";
  quickReplies?: Array<{
    text: string;
    nextMessageId?: string;
  }>;
  repliesHidden?: boolean;
}

const useConversationFlow = () => {
  const { activeBrandID } = useContext(CurrentUserContext);
  const [drawerContent, setDrawerContent] = useState<string | null>(null);

  const handleDrawerOpen = (route: string) => {
    setDrawerContent(route);
  };

  const initialMessages: Message[] = useMemo(
    () => [
      {
        id: "1",
        text: (
          <>
            <Typography variant="body1">👋 Welcome!</Typography>
            <Typography variant="body1">
              Let's get your app looking amazing and working seamlessly. We'll
              go step-by-step, starting with building your app so everything is
              ready before we publish it.
              <br />
              <br />
              Are you ready to start customizing your app?
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [{ text: "Let's do it", nextMessageId: "2" }],
      },
      {
        id: "2",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">Step 1: Design Your Navigation</Typography>
            <Typography variant="body1">
              We'll start by creating a clear, intuitive navigation system for
              your mobile app so users can find everything easily.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => handleDrawerOpen(BUILD_NAVIGATION)}
            >
              Update Navigation
            </Button>
            <Typography variant="body1">
              When you're finished, come back here and tell me:
            </Typography>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [{ text: "I'm done", nextMessageId: "3" }],
      },
      {
        id: "3",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">Step 2: Customize Your Visuals</Typography>
            <Typography variant="body1">
              Now let's make your app look like your brand. Pick your colors,
              logo, fonts, and other visual components.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => handleDrawerOpen(BUILD_THEME)}
            >
              Update Theme
            </Button>
            <Typography variant="body1">
              When you're finished, come back here and tell me:
            </Typography>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [{ text: "I'm done", nextMessageId: "4" }],
      },
      {
        id: "4",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">
              Step 3: Add Third-Party Integrations
            </Typography>
            <Typography variant="body1">
              Connect your app to services like Shopify, Klaviyo, and more to
              boost its functionality.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => handleDrawerOpen(SETTINGS_INTEGRATIONS)}
            >
              Update Integrations
            </Button>
            <Typography variant="body1">
              When you're finished, come back here and tell me:
            </Typography>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I'm done", nextMessageId: "5" },
          { text: "Skip for now", nextMessageId: "5" },
        ],
      },
      {
        id: "5",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">
              Step 4: Create "For You" Tab Content
            </Typography>
            <Typography variant="body1">
              Let's add some personalized feed content to engage your users.
              Create a feed post with updates, announcements, or other content
              your users would find valuable.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => handleDrawerOpen(ENGAGE_FEED_CREATE)}
            >
              Create Feed Post
            </Button>
            <Typography variant="body1">
              When you're finished, come back here and tell me:
            </Typography>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I'm done", nextMessageId: "6" },
          { text: "Skip for now", nextMessageId: "6" },
        ],
      },
      {
        id: "6",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">
              Step 5: Set Up Push Notifications
            </Typography>
            <Typography variant="body1">
              Let's create your first push notification to engage with your
              users. Craft a message that delivers value - whether it's
              highlighting new features, providing recommendations, or offering
              exclusive deals.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => handleDrawerOpen(ENGAGE_NOTIFICATIONS)}
            >
              Create Push Notification
            </Button>
            <Typography variant="body1">
              When you're finished, come back here and tell me:
            </Typography>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I'm done", nextMessageId: "7" },
          { text: "Skip for now", nextMessageId: "7" },
        ],
      },
      {
        id: "7",
        text: (
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="h6">🔧 Let's test your app</Typography>
            <Typography variant="body1">
              First, download Kin Studio on your mobile device:
            </Typography>
            <Stack direction="row" spacing={2}>
              <Stack direction="column" spacing={2} alignItems="center">
                <QRCode
                  size={256}
                  style={{ height: "auto", width: "150px" }}
                  value={
                    "https://apps.apple.com/us/app/kin-studio/id6455128364"
                  }
                  viewBox={`0 0 256 256`}
                />
                <Typography variant="body1" sx={{ textAlign: "center" }}>
                  iOS
                </Typography>
              </Stack>
              <Stack direction="column" spacing={2} alignItems="center">
                <QRCode
                  size={256}
                  style={{ height: "auto", width: "150px" }}
                  value={
                    "https://play.google.com/store/apps/details?id=com.kin.studio"
                  }
                  viewBox={`0 0 256 256`}
                />
                <Typography variant="body1" sx={{ textAlign: "center" }}>
                  Android
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've installed Kin Studio", nextMessageId: "8" },
        ],
      },
      {
        id: "8",
        text: (
          <>
            <Typography variant="body1">
              Great! Now open Kin Studio and tap the scan button. Then scan your
              app's QR code to preview it:
            </Typography>
            <QRCode
              size={256}
              style={{
                height: "auto",
                width: "150px",
                marginTop: "16px",
              }}
              value={
                "kinstudio://load-brand?id=" +
                window.atob(activeBrandID).split(":")[1]
              }
              viewBox={`0 0 256 256`}
            />
            <Typography variant="body1">
              When you're finished previewing your app, we'll move on to the
              next step.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "Continue to App Store Setup", nextMessageId: "9" },
        ],
      },
      {
        id: "9",
        text: (
          <>
            <Typography variant="h6">App Store Setup</Typography>
            <Typography variant="body1">
              Now let's make sure it's prepared for the App Stores so users can
              download it.
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              App Store Setup
            </Typography>
            <Typography variant="body1">
              First, we'll need to set up your Apple and Google Developer
              accounts so your app can be published when it's ready. Have you
              already created both accounts?
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "Yes, both are done", nextMessageId: "11" },
          { text: "No, help me create them", nextMessageId: "10" },
        ],
      },
      {
        id: "10",
        text: (
          <>
            <Typography variant="body1">
              No problem — I'll guide you through each step. To publish apps,
              you need accounts on both platforms:
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              🧷 Apple Developer Program →{" "}
              <a
                href="https://developer.apple.com/help/account/membership/program-enrollment"
                target="_blank"
                rel="noopener noreferrer"
              >
                Follow the instructions here
              </a>
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              🧷 Google Play Console →{" "}
              <a
                href="https://support.google.com/googleplay/android-developer/answer/6112435?hl=en"
                target="_blank"
                rel="noopener noreferrer"
              >
                Follow the instructions here
              </a>
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Let me know once you've completed both.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [{ text: "I've set up both", nextMessageId: "11" }],
      },
      {
        id: "11",
        text: (
          <>
            <Typography variant="body1">
              💡 Great — App Store Setup is done! Next up — let's submit your
              app to the stores so customers can start downloading it.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "Skip for now", nextMessageId: "16" },
          { text: "Proceed to next step", nextMessageId: "12" },
        ],
      },
      {
        id: "12",
        text: (
          <>
            <Typography variant="h6">
              🚀 Nice work building your app!
            </Typography>
            <Typography variant="body1">
              Now, let's submit it so users can download it from the App Store
              and Google Play. We've got 3 quick steps.
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Step 1: Optimize Your App Listing
              <br />
              We'll polish your app name, description, screenshots, keywords,
              and category so your listing attracts more downloads.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "13" },
        ],
      },
      {
        id: "13",
        text: (
          <>
            <Typography variant="h6">
              Step 2: Submit Your App for Review
            </Typography>
            <Typography variant="body1">
              Once your listing is ready, we'll follow Apple and Google's
              submission guidelines to upload your app package for approval.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "14" },
        ],
      },
      {
        id: "14",
        text: (
          <>
            <Typography variant="h6">Step 3: Launch Your App</Typography>
            <Typography variant="body1">
              After your app is approved, it's go time! We'll set a launch date
              so users can download, install, and start enjoying it.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "15" },
        ],
      },
      {
        id: "15",
        text: (
          <>
            <Typography variant="h6">
              🎉 Congratulations — your app is now live and ready for the world!
            </Typography>
            <Typography variant="body1">
              Next up: let's plan your marketing so you can reach your audience
              fast.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "Skip for now", nextMessageId: "19" },
          { text: "Go to marketing", nextMessageId: "16" },
        ],
      },
      {
        id: "16",
        text: (
          <>
            <Typography variant="body1">
              Let's walk through the Marketing phase to help you attract users
              and boost engagement.
            </Typography>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Step 1: Offer Exclusive In-App Incentives
            </Typography>
            <Typography variant="body1">
              Create rewards only available inside your app — such as special
              discounts, promotions, loyalty rewards, or exclusive content — to
              motivate downloads and keep users coming back.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "17" },
        ],
      },
      {
        id: "17",
        text: (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Step 2: Promote Your Mobile App
            </Typography>
            <Typography variant="body1">
              Run a marketing campaign using social media, influencers, ads,
              email marketing, and cross-promotion to spread the word and
              increase installs.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "18" },
        ],
      },
      {
        id: "18",
        text: (
          <>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Step 3: Engage Users with Your First Push Notification
            </Typography>
            <Typography variant="body1">
              Craft a short and engaging push notification to highlight
              features, give recommendations, or share incentives with your
              audience.
            </Typography>
          </>
        ),
        sender: "bot",
        quickReplies: [
          { text: "I've completed this step", nextMessageId: "19" },
        ],
      },
      {
        id: "19",
        text: (
          <>
            <Typography variant="h6">🎉 Setup is complete!</Typography>
            <Typography variant="body1">
              Congratulations! You've completed the essential steps to launch
              your app successfully. You're now ready to grow your user base and
              create engaging experiences for your audience.
            </Typography>
          </>
        ),
        sender: "bot",
      },
    ],
    [activeBrandID]
  );
  return { initialMessages, drawerContent, setDrawerContent };
};

const ChatMessage = styled(Box)<{ $isUser?: boolean; $width?: string }>`
  width: ${({ $width }) => $width || "auto"};
  max-width: min(70%, 100%);
  min-width: 0;
  overflow-wrap: anywhere;
  padding: ${({ $isUser }) => ($isUser ? "8px 12px" : "0 12px")};
  border-radius: ${({ $isUser }) => ($isUser ? "8px 0 8px 8px" : "none")};
  align-self: ${({ $isUser }) => ($isUser ? "flex-end" : "flex-start")};
  background-color: ${({ $isUser, theme }) =>
    $isUser ? theme.palette.secondary.main : "#FFFFFF80"};
  border: ${({ $isUser }) =>
    $isUser ? "1px solid var(--black-8, #00000014)" : "none"};
  color: ${({ $isUser, theme }) =>
    $isUser ? theme.palette.common.white : "#121111"};
`;

const ResponseButtonsContainer = styled(Stack)`
  width: 100%;
  flex-wrap: wrap;
  padding: 0 12px;
`;

const ResponseButton = styled(Button)`
  border-radius: 12px;
  background: #5048e514;
  color: #5048e5;
  &:hover {
    background: "rgba(0, 0, 0, 0.1)";
  }
`;

const Stepper = ({ activeStep }: { activeStep: number }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={0}
      sx={{
        mx: { xs: 1, md: 6 },
        width: { xs: "calc(100% - 16px)", md: "auto" },
        maxWidth: "100%",
        flexShrink: 0,
        borderRadius: "0 0 12px 12px",
        overflowX: "auto",
        boxShadow: "0px 5px 40px 0px #0000000D",
      }}
    >
      {steps.map((step, i) => {
        const isCompleted = activeStep > i + 1;
        const isActive = activeStep === i + 1;

        const icon = isCompleted ? <CheckIcon /> : <Box>0{step.number}</Box>;
        return (
          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            alignItems="center"
            key={i}
            sx={{
              flex: { xs: "1 1 0", md: 1 },
              minWidth: 0,
              padding: { xs: "12px 8px", md: "16px" },
              borderLeft: i === 0 ? "1px solid #DDDDDD" : "none",
              borderRight: "1px solid #DDDDDD",
              borderRadius:
                i === steps.length - 1
                  ? "0 0 12px 0"
                  : i === 0
                  ? "0 0 0 12px"
                  : "0",
              bgcolor: "white",
              borderBottom: isActive
                ? "2px solid #5A62B8"
                : "1px solid #DDDDDD",
            }}
          >
            <Box
              sx={{
                width: { xs: 28, md: 40 },
                height: { xs: 28, md: 40 },
                borderWidth: 2,
                borderRadius: 20,
                flexShrink: 0,
                border: "2px solid",
                borderColor:
                  isActive || isCompleted
                    ? theme.palette.secondary.main
                    : theme.palette.grey[300],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: { xs: "12px", md: "inherit" },
                color: isActive
                  ? theme.palette.secondary.main
                  : isCompleted
                  ? theme.palette.common.white
                  : theme.palette.grey[300],
                backgroundColor: isCompleted
                  ? theme.palette.secondary.main
                  : "transparent",
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color={isActive ? theme.palette.secondary.main : "black"}
                sx={{
                  fontSize: { xs: "12px", md: "1rem" },
                  lineHeight: 1.2,
                }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                color={
                  isActive
                    ? theme.palette.secondary.main
                    : theme.palette.grey[600]
                }
                sx={{
                  display: { xs: "none", md: "block" },
                }}
              >
                {step.subtitle}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

const Step0Content = ({
  setActiveStep,
}: {
  setActiveStep: (step: number) => void;
}) => {
  const theme = useTheme();

  return (
    <Stack
      spacing={{ xs: 4, md: 8 }}
      alignItems="center"
      width="100%"
      maxWidth="100%"
      height="100%"
      justifyContent="flex-end"
      px={{ xs: 2, md: 0 }}
      overflow="hidden"
    >
      <Stack
        spacing={4}
        alignItems="center"
        maxWidth="980px"
        width="100%"
        textAlign="center"
        flexShrink={0}
      >
        <Typography
          variant="h2"
          fontWeight="bold"
          sx={{ fontSize: { xs: "2rem", md: "3.75rem" } }}
        >
          Welcome to{" "}
          <Box component="span" sx={{ color: theme.palette.primary.main }}>
            Kin
          </Box>
        </Typography>

        <Typography variant="body1">
          Follow the steps to set up, build, submit, and launch your app
          successfully. Each part ensures your app is ready for users and meets
          all requirements for a smooth release.
        </Typography>

        <Button
          variant="contained"
          size="large"
          onClick={() => setActiveStep(1)}
        >
          Get Started
        </Button>
      </Stack>
      <Box
        component="img"
        src="https://kinntech.blob.core.windows.net/kinn-public-assets/phone_asset.svg"
        alt="Phone illustration"
        sx={{
          width: "auto",
          maxWidth: "100%",
          height: { xs: 260, md: 400 },
          display: "block",
        }}
      />
    </Stack>
  );
};

const GetStartedTab2 = () => {
  const { initialMessages, drawerContent, setDrawerContent } =
    useConversationFlow();
  const dispatch = useMobilePreviewDispatch();

  useEffect(() => {
    dispatch({
      type: ReducerAction.SET_PREVIEW_TYPE,
      payload: {
        previewType: PreviewType.DASHBOARD,
      },
    });

    return () => {
      dispatch({
        type: ReducerAction.RESET,
      });
    };
  }, [dispatch]);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [activeStep, setActiveStep] = useState(0);

  const [messages, setMessages] = useState<Message[]>([initialMessages[0]]);

  // Update active step based on current message ID
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === "bot") {
        const currentMessageId = lastMessage.id;
        const step = steps.find((step) => {
          const [start, end] = step.messageRange;
          return (
            Number(currentMessageId) >= Number(start) &&
            Number(currentMessageId) <= Number(end)
          );
        });
        if (initialMessages.length <= Number(currentMessageId)) {
          setActiveStep(steps.length + 1);
        } else {
          if (step) {
            setActiveStep(step.number);
          }
        }
      }
    }
  }, [messages, initialMessages.length]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const content = useMemo(() => {
    switch (activeStep) {
      case 0:
        return <Step0Content setActiveStep={setActiveStep} />;
      default:
        return (
          <Stack
            spacing={1}
            sx={{
              width: "100%",
              maxWidth: "100%",
              flex: 1,
              overflowX: "hidden",
              overflowY: "auto",
              py: { xs: 3, md: 6 },
              px: { xs: 2, md: 14 },
              boxSizing: "border-box",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "&:hover": {
                "&::-webkit-scrollbar": {
                  display: "block",
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#888",
                  borderRadius: "4px",
                },
              },
              msOverflowStyle: "-ms-autohiding-scrollbar", // IE and Edge
              scrollbarWidth: "thin", // Firefox
            }}
          >
            {messages.map((msg) => (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {msg.sender === "bot" && (
                  <Box
                    component="img"
                    src="https://kinntech.blob.core.windows.net/kinn-public-assets/chat_profile_picture.png"
                    alt="Bot avatar"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                    }}
                  />
                )}
                <Stack direction="column" spacing={1} width="100%">
                  <ChatMessage $isUser={msg.sender === "user"}>
                    {msg.text}
                  </ChatMessage>

                  {msg.quickReplies && !msg.repliesHidden && (
                    <ResponseButtonsContainer spacing={1} direction="row">
                      {msg.quickReplies.map((option, index) => {
                        return (
                          <ResponseButton
                            key={index}
                            size="small"
                            variant="contained"
                            onClick={() => {
                              setMessages(
                                messages.map((m) =>
                                  m.id === msg.id
                                    ? { ...m, repliesHidden: true }
                                    : m
                                )
                              );
                              setMessages((prev) => [
                                ...prev,
                                {
                                  id: Date.now().toString(),
                                  text: option.text,
                                  sender: "user",
                                },
                              ]);

                              // Find and add next bot message after delay
                              setTimeout(() => {
                                if (option.nextMessageId) {
                                  const nextMessage = initialMessages.find(
                                    (m) => m.id === option.nextMessageId
                                  );
                                  if (nextMessage) {
                                    setMessages((prev) => [
                                      ...prev,
                                      nextMessage,
                                    ]);
                                  }
                                }
                              }, 200);
                            }}
                          >
                            {option.text}
                          </ResponseButton>
                        );
                      })}
                    </ResponseButtonsContainer>
                  )}
                </Stack>
              </Stack>
            ))}

            <div ref={messagesEndRef} />
          </Stack>
        );
    }
  }, [activeStep, messages, initialMessages]);

  return (
    <Stack
      alignItems="center"
      sx={{
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        backgroundColor: "white",
      }}
    >
      <Stepper activeStep={activeStep} />

      {content}
      {/* {activeStep > 0 && chatInput} */}

      <GetStartedTab2BottomSheet
        drawerContent={drawerContent}
        setDrawerContent={setDrawerContent}
      />
    </Stack>
  );
};

export default GetStartedTab2;
